import { marker } from "@jsverse/transloco-keys-manager/marker";
import { lastValueFrom } from "rxjs";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";
import { Physician } from "src/app/backend/models/physician";
import { PopupService } from "src/app/services/popup/popup.service";
import { sntTranslate } from "src/app/support/translate";

export const DirectoryPhysicianSendRoles = ["external", "referring"] as const;
export type DirectoryPhysicianSendRole =
  (typeof DirectoryPhysicianSendRoles)[number];

type DirectoryPhysician = Pick<
  Physician,
  "id" | "firstName" | "lastName" | "email"
>;

type DirectoryPhysicianSendProviders = {
  popupService: PopupService;
  physicianBackend: PhysicianBackend;
};

const MissingDirectoryPhysicianEmailCopy = {
  external: {
    title: marker("Email del medico esterno mancante"),
    body: marker(
      "Al pet è assegnato un medico esterno senza indirizzo e-mail in anagrafica. Puoi inserirlo ora oppure proseguire senza: la scheda verrà comunque inviata al proprietario."
    ),
    label: marker("Email medico esterno")
  },
  referring: {
    title: marker("Email del medico referente mancante"),
    body: marker(
      "Al pet è assegnato un medico referente senza indirizzo e-mail in anagrafica. Puoi inserirlo ora oppure proseguire senza: la scheda verrà comunque inviata al proprietario."
    ),
    label: marker("Email medico referente")
  }
} as const;

export async function resolveDirectoryPhysicianSendEmail(
  physician: DirectoryPhysician | undefined,
  role: DirectoryPhysicianSendRole,
  providers: DirectoryPhysicianSendProviders
): Promise<string | undefined> {
  if (!physician) {
    return undefined;
  }

  const existingEmail = physician.email?.trim();
  if (existingEmail) {
    return existingEmail;
  }

  const email = await promptMissingDirectoryPhysicianEmail(
    physician,
    role,
    providers
  );
  if (!email) {
    return undefined;
  }

  physician.email = email;

  return email;
}

export async function resolveExternalPhysicianSendEmail(
  physician: DirectoryPhysician | undefined,
  providers: DirectoryPhysicianSendProviders
): Promise<string | undefined> {
  return resolveDirectoryPhysicianSendEmail(physician, "external", providers);
}

export async function resolveReferringPhysicianSendEmail(
  physician: DirectoryPhysician | undefined,
  providers: DirectoryPhysicianSendProviders
): Promise<string | undefined> {
  return resolveDirectoryPhysicianSendEmail(physician, "referring", providers);
}

async function promptMissingDirectoryPhysicianEmail(
  physician: DirectoryPhysician,
  role: DirectoryPhysicianSendRole,
  providers: DirectoryPhysicianSendProviders
): Promise<string | undefined> {
  let resolvedEmail: string | undefined;
  const copy = MissingDirectoryPhysicianEmailCopy[role];

  await providers.popupService.createEntity<{ email: string }>({
    title: sntTranslate(copy.title),
    confirmText: sntTranslate(marker("Salva e invia")),
    rejectText: sntTranslate(marker("Salta e invia comunque")),
    rejectOnLeft: true,
    modalConfig: {
      dialog: true,
      width: 480,
      height: 320,
      icon: "fa-light fa-triangle-exclamation",
      zIndex: 60
    },
    initialFormData: {
      email: ""
    },
    formItems: [
      {
        template: (_data: unknown, element: HTMLElement) => {
          element.innerHTML = `<p class="m-0 mb-3 text-sm text-slate-600">${sntTranslate(
            copy.body
          )}</p>`;
        }
      },
      {
        dataField: "email",
        editorType: "dxTextBox",
        editorOptions: {
          placeholder: "nome@clinica.it"
        },
        label: {
          text: sntTranslate(copy.label),
          location: "top",
          showColon: false
        },
        validationRules: [{ type: "required" }, { type: "email" }]
      }
    ],
    create: async (data) => {
      const email = data.email?.trim();
      if (!email) {
        return;
      }

      await lastValueFrom(
        providers.physicianBackend.update(physician.id, {
          firstName: physician.firstName,
          lastName: physician.lastName,
          email
        })
      );

      resolvedEmail = email;
    }
  });

  return resolvedEmail;
}
